import { type Session, type SupabaseClient } from '@supabase/supabase-js'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { getSupabase } from '../client'
import { atCap as atCapSnapshot, capHasRoom } from '../lib/planCapGate'
import type { CapKey, TenantPlanContext } from '../types/planContext'

type SessionError = {
  error: string
  message: string
}

/**
 * Full typed Supabase context.
 * - Core auth fields are always present.
 * - Session fields (user, tenant, permissions …) are populated by the
 *   web app's combined SupabaseProvider; the base provider supplies safe stubs.
 */
export interface SupabaseContextType {
  // --- Core SDK ---
  supabase: SupabaseClient
  auth: SupabaseClient['auth']
  session: Session | null
  /** true until initial auth state is known */
  loading: boolean
  sessionErrors: SessionError[]
  apiURL: string
  projectRef: string
  refreshSession: () => Promise<void>

  // --- auth / session actions ---
  signOut: (
    onBeforeSignOut?: () => void,
    onAfterSignOut?: () => void
  ) => Promise<void>

  // --- Session data (filled by app-level provider) ---
  // user is always a SessionUser when loading:false and the session exists.
  // Components relying on user are only rendered in that state, so no optional chaining needed.
  user: SessionUser
  tenant: Tenant | undefined
  tenancies: UserTenancy[]

  // --- JWT claims ---
  permissions: string[]
  /** Enabled feature keys (ICP + tenant customization); empty until JWT refresh after features rollout */
  features: string[]
  isOwner: boolean
  roleId: string | null
  tenantId: string | null
  /** Active tenant membership: pending | active | suspended (per-tenant, not global). */
  membershipStatus: 'pending' | 'active' | 'suspended' | null

  // --- Permission helpers ---
  hasPermission: (permission: string | boolean | undefined) => boolean
  hasFeature: (permission: string | boolean | undefined) => boolean
  hasRole: (role: string) => boolean
  isAdmin: () => boolean
  /** Shorthands */
  p: (permission: string | boolean | undefined) => boolean
  f: (feature: string | boolean | undefined) => boolean

  /** ICP × plan × caps (live meters; modules still use p()/JWT). */
  plan: TenantPlanContext | null
  planLoading: boolean
  refreshPlan: () => Promise<void>
  capHasRoom: (key: CapKey | string) => boolean
  atCap: (key: CapKey | string) => boolean

  // --- Data actions ---
  updateUser: (data: Partial<User>) => Promise<User | null>
  updateTenant: (data: Partial<Tenant>) => Promise<Tenant | null>
  refreshUser: () => Promise<void>
  refreshTenant: () => Promise<void>
  refreshPermissions: () => Promise<void>
  switchTenant: (tenantId: string) => Promise<void>
  getUser: () => SessionUser

  // --- Hints ---
  /** Returns true when the hint has not yet been dismissed by this user. */
  isHintVisible: (id: string) => boolean
  /** Marks a hint as dismissed in user.meta.settings.hints and persists to DB. */
  dismissHint: (id: string) => Promise<void>
  /** Clears all dismissed hints — useful for testing / user profile reset. */
  resetHints: () => Promise<void>

  // --- Utilities ---
  userLanguage: string
  tenantBucket: string
  aiEnabled: boolean
  getBucket: ReturnType<SupabaseClient['storage']['from']>
  getAvatarUrl: (
    id: string,
    options?: { width: number; height: number }
  ) => string | null
  listBucketContents: (bucket: string) => Promise<FileList>
}

type SupabaseProviderProps = {
  children: ReactNode
  config: {
    apiURL: string
    anonKey: string
    projectRef: string
  }
}

export const SupabaseContext = createContext<SupabaseContextType | undefined>(
  undefined
)

/**
 * Base SupabaseProvider for Circle and Admin apps.
 * Provides auth state only; session fields are safe no-op stubs.
 * The web app overrides this with its own combined provider.
 */
export const SupabaseProvider = ({
  children,
  config
}: SupabaseProviderProps) => {
  const supabase = useMemo(
    () => getSupabase(config.apiURL, config.anonKey),
    [config.apiURL, config.anonKey]
  )

  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [sessionErrors, setSessionErrors] = useState<SessionError[]>([])

  const refreshSession = useCallback(async () => {
    const { error } = await supabase.auth.refreshSession()
    if (error) {
      setSessionErrors([{ error: 'refresh_failed', message: error.message }])
    }
  }, [supabase.auth])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
  }, [supabase.auth])

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session)
        setLoading(false)
      })
      .catch((error) => {
        console.error('⚠️ Error getting session:', error)
        setSessionErrors([
          {
            error: 'get_session_failed',
            message: error?.message || 'Failed to get session'
          }
        ])
        setLoading(false)
      })

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') setSession(null)
      else setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [supabase.auth])

  const getBucket = useMemo(() => supabase.storage.from(''), [supabase.storage])

  // todo: trim supabase down to only supabase related data, expect apps to build their own session contexts on top of the supabase context
  const value: SupabaseContextType = useMemo(
    () => ({
      supabase,
      auth: supabase.auth,
      session,
      loading,
      sessionErrors,
      apiURL: config.apiURL,
      projectRef: config.projectRef,
      refreshSession,
      signOut,
      // Session stubs — not populated in the base provider
      user: {} as SessionUser,
      tenant: undefined,
      tenancies: [],
      permissions: [],
      features: [],
      isOwner: false,
      roleId: null,
      tenantId: null,
      membershipStatus: null,
      hasPermission: () => false,
      hasFeature: () => false,
      p: () => false,
      f: () => false,
      plan: null,
      planLoading: false,
      refreshPlan: async () => {},
      capHasRoom: () => true,
      atCap: () => false,
      updateUser: async () => null,
      updateTenant: async () => null,
      switchTenant: async () => {},
      getUser: () => ({}) as SessionUser,
      userLanguage: 'en',
      tenantBucket: '',
      getBucket,
      getAvatarUrl: () => null,
      listBucketContents: async () => [] as any
    }),
    [
      supabase,
      session,
      loading,
      config.apiURL,
      config.projectRef,
      refreshSession,
      signOut,
      getBucket
    ]
  )

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  )
}

export const useSupabase = () => {
  const context = useContext(SupabaseContext)
  if (context === undefined) {
    throw new Error('useSupabase must be used within a SupabaseProvider')
  }
  return context
}

/** Kept for Circle / Admin session contexts that only consume auth fields. */
export const useSupabaseCore = () => {
  const context = useContext(SupabaseContext)
  if (context === undefined) {
    throw new Error('useSupabaseCore must be used within a SupabaseProvider')
  }
  return {
    supabase: context.supabase,
    auth: context.auth,
    session: context.session,
    sessionErrors: context.sessionErrors,
    apiURL: context.apiURL,
    projectRef: context.projectRef,
    refreshSession: context.refreshSession,
    signOut: context.signOut,
    loading: context.loading
  }
}
